/**
 * Small score-entry helpers shared by the mobile and web match screens.
 * Keep the UI indicator tied to the same per-hole stroke map that the API
 * uses when it calculates net scores.
 */

export type HoleStrokeMap = Record<string, number> | null | undefined;

export interface StrokeAllocationHole {
  holeNumber: number;
  strokes: number;
}

export interface StrokeAllocationSummary {
  totalStrokes: number;
  holes: StrokeAllocationHole[];
}

export function strokesReceivedOnHole(map: HoleStrokeMap, holeNumber: number): number {
  if (!map) return 0;
  const value = map[String(holeNumber)];
  const strokes = Number(value);
  return Number.isFinite(strokes) && strokes > 0 ? strokes : 0;
}

export function hasPopOnHole(map: HoleStrokeMap, holeNumber: number): boolean {
  return strokesReceivedOnHole(map, holeNumber) > 0;
}

export function sideHasPopOnHole(
  participants: readonly { sideNumber: number; receivesStrokesJson: HoleStrokeMap }[],
  sideNumber: number,
  holeNumber: number,
): boolean {
  return participants.some(
    (participant) => participant.sideNumber === sideNumber
      && hasPopOnHole(participant.receivesStrokesJson, holeNumber),
  );
}

export function strokeAllocationSummary(map: HoleStrokeMap): StrokeAllocationSummary {
  const holes = Array.from({ length: 18 }, (_, index) => index + 1)
    .map((holeNumber) => ({
      holeNumber,
      strokes: strokesReceivedOnHole(map, holeNumber),
    }))
    .filter((hole) => hole.strokes > 0);
  return {
    totalStrokes: holes.reduce((total, hole) => total + hole.strokes, 0),
    holes,
  };
}

/**
 * Shared-ball formats apply one side score. Use the largest allocation on a
 * side for each hole so duplicate participant rows never double-count a team
 * pop in the explanation or indicator.
 */
export function sideStrokeAllocationSummary(
  participants: readonly { sideNumber: number; receivesStrokesJson: HoleStrokeMap }[],
  sideNumber: number,
): StrokeAllocationSummary {
  const holes = Array.from({ length: 18 }, (_, index) => index + 1)
    .map((holeNumber) => ({
      holeNumber,
      strokes: participants
        .filter((participant) => participant.sideNumber === sideNumber)
        .reduce((largest, participant) => Math.max(
          largest,
          strokesReceivedOnHole(participant.receivesStrokesJson, holeNumber),
        ), 0),
    }))
    .filter((hole) => hole.strokes > 0);
  return {
    totalStrokes: holes.reduce((total, hole) => total + hole.strokes, 0),
    holes,
  };
}
